import { httpsCallable } from "firebase/functions"
import {
  AddCabinToBasketInput,
  AddCabinToBasketOutput,
  AgentProfileActionInput,
  BookCabinsInput,
  BookCabinsOutput,
  CreateStripePaymentIntentInput,
  CreateStripePaymentIntentOutput,
  CreateTripInput,
  CreateTripOutput,
  DeleteAccountInput,
  DeleteAccountOutput,
  DeleteUserDeviceTokenInput,
  DeleteUserDeviceTokenOutput,
  FindPortsInput,
  FindPortsOutput,
  FunctionOutput,
  GenerateAuthLinkInput,
  GenerateAuthLinkOutput,
  GetProfilesInput,
  GetProfilesOutput,
  JoinTripInput,
  JoinTripResult,
  LikeSearchResultInput,
  LikeSearchResultOutput,
  LoyaltyProgramActionInput,
  SaveUserDeviceTokenInput,
  SaveUserDeviceTokenOutput,
  SearchDeparturePortsInput,
  SearchDeparturePortsOutput,
  SearchDestinationsInput,
  SearchDestinationsOutput,
  SearchResultCabins,
  SearchResultCabinsInput,
  SearchResultCabinsSummaryInput,
  SearchResultCabinsSummaryOutput,
  SearchResultDetails,
  SearchResultDetailsInput,
  TripRoomActionInput,
  TwilioAccessTokenInput,
  TwilioAccessTokenResult,
  BillingActionInput,
  BookingActionInput,
  DictionariesActionInput,
  FunctionOutputCode,
  TrackAnalyticsEventInput,
  TrackAnalyticsEventOutput,
  UserProfileActionInput,
  ExchangeAuthTokenInput,
  ExchangeAuthTokenOutput,
  GenerateWebPreviewUrlInput,
  GenerateWebPreviewUrlOutput,
  RegisterUserInput,
  RegisterUserOutput,
} from "@cruisen/contract"
import { _t } from "@cruisen/i18n"
import { functions } from "./firebase"

type FirebaseFunctions = {
  generateAuthLink: {
    payload: GenerateAuthLinkInput
    result: GenerateAuthLinkOutput
  }
  registerUser: { payload: RegisterUserInput; result: RegisterUserOutput }
  exchangeAuthToken: {
    payload: ExchangeAuthTokenInput
    result: ExchangeAuthTokenOutput
  }
  saveUserDeviceToken: {
    payload: SaveUserDeviceTokenInput
    result: SaveUserDeviceTokenOutput
  }
  deleteUserDeviceToken: {
    payload: DeleteUserDeviceTokenInput
    result: DeleteUserDeviceTokenOutput
  }
  deleteAccount: {
    payload: DeleteAccountInput
    result: DeleteAccountOutput
  }
  departurePorts: {
    payload: SearchDeparturePortsInput
    result: SearchDeparturePortsOutput
  }
  destinationPorts: {
    payload: SearchDestinationsInput
    result: SearchDestinationsOutput
  }
  findPorts: { payload: FindPortsInput; result: FindPortsOutput }
  createTrip: { payload: CreateTripInput; result: CreateTripOutput }
  joinTrip: { payload: JoinTripInput; result: JoinTripResult }
  tripSearchResultCabins: {
    payload: SearchResultCabinsInput
    result: SearchResultCabins
  }
  tripSearchResultCabinsSummary: {
    payload: SearchResultCabinsSummaryInput
    result: SearchResultCabinsSummaryOutput
  }
  tripSearchResultDetails: {
    payload: SearchResultDetailsInput
    result: SearchResultDetails
  }
  twilioAccessToken: {
    payload: TwilioAccessTokenInput
    result: TwilioAccessTokenResult
  }
  getProfiles: { payload: GetProfilesInput; result: GetProfilesOutput }
  tripRoomAction: { payload: TripRoomActionInput; result: FunctionOutput }
  likeSearchResult: {
    payload: LikeSearchResultInput
    result: LikeSearchResultOutput
  }
  addCabinToBasket: {
    payload: AddCabinToBasketInput
    result: AddCabinToBasketOutput
  }
  bookCabins: { payload: BookCabinsInput; result: BookCabinsOutput }
  validateBooking: { payload: BookCabinsInput; result: BookCabinsOutput }
  createStripePaymentIntent: {
    payload: CreateStripePaymentIntentInput
    result: CreateStripePaymentIntentOutput
  }
  loyaltyProgramAction: {
    payload: LoyaltyProgramActionInput
    result: FunctionOutput
  }
  billingAction: { payload: BillingActionInput; result: FunctionOutput }
  bookingAction: { payload: BookingActionInput; result: FunctionOutput }
  agentProfileAction: {
    payload: AgentProfileActionInput
    result: FunctionOutput
  }
  dictionariesAction: {
    payload: DictionariesActionInput
    result: FunctionOutput
  }
  trackAnalyticsEvent: {
    payload: TrackAnalyticsEventInput
    result: TrackAnalyticsEventOutput
  }
  userProfileAction: {
    payload: UserProfileActionInput
    result: FunctionOutput
  }
  generateWebPreviewUrl: {
    payload: GenerateWebPreviewUrlInput
    result: GenerateWebPreviewUrlOutput
  }
}

type FirebaseFunctionResult<T extends keyof FirebaseFunctions> =
  | { error: false; data: FirebaseFunctions[T]["result"] }
  | {
      error: true
      code: FunctionOutputCode
      data: null
      message: string
      details?: Record<string, unknown>
    }

export function isFunctionOutput(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  object: any,
): object is FunctionOutput<unknown, { isSupportedAppVersion?: boolean }> {
  return typeof object === "object" && typeof object.success === "boolean" && object.code
}

export const callFirebaseFunction = async <T extends keyof FirebaseFunctions>({
  name,
  payload,
  options,
}: {
  name: T
  payload: FirebaseFunctions[T]["payload"]
  options?: {
    timeoutSeconds?: number
    firebaseExtension?: string
  }
}): Promise<FirebaseFunctionResult<T>> => {
  console.log(
    `[${name}${payload && payload.action ? `:${payload.action}` : ""}] call firebase function`,
  )
  try {
    const timeout = options?.timeoutSeconds ? options?.timeoutSeconds * 1000 : undefined
    const firebaseFunctionName = options?.firebaseExtension
      ? `${options?.firebaseExtension}-${name}`
      : name
    const response = await httpsCallable(functions, firebaseFunctionName, { timeout })(payload)
    const data = response.data as FirebaseFunctions[T]["result"]

    if (isFunctionOutput(data)) {
      if (!data.success) {
        console.error(`[${name}] firebase http function error: `, {
          error: new Error(
            `[${name}] firebase function call failure: ${JSON.stringify({
              message: data.message,
              code: data.code,
              details: data.details,
            })}`,
          ),
        })

        return {
          error: true,
          code: data.code,
          data: null,
          message: data.message ?? _t("errorScreen.title"),
          details: data.details,
        }
      }
    }

    return { error: false, data }
  } catch (error) {
    console.error(
      `[${name}]${
        payload && payload.action ? `:${payload.action}` : ""
      }] firebase function error: `,
      { error },
    )
    return {
      error: true,
      code: "unknown",
      data: null,
      message: (error as Error).toString(),
    }
  }
}
